import { isDoorUnitCurved } from "building-generation/elements/doors/doorUtils";
import type { Building } from "building-types/building/buildingTypes";
import type { DoorUnit } from "building-types/doors/doorTypes";
import type { BuildingGeometryEmitter, MaterialSlotRemapping } from "building-types/geometry/buildingGeometryTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { HomeSettings } from "building-types/settings/homeSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceGroupNew } from "geom/brep";
import { geomToFaces } from "geom/geom";
import {
  buildExteriorLayers,
  buildInteriorLayers,
  emitBuildingProp,
  getInteriorGroupsGeometries,
  getExteriorGroupsGeometries,
} from "./buildingGeometryTools";

/**
 * Determine the face category for a door based on curvature and exterior status.
 */
export function getDoorFaceCategory(isCurved: boolean, isExterior: boolean): FaceCategory {
  if (isCurved) {
    return isExterior ? FaceCategory.DOOR_CURVED_WALL_EXTERIOR : FaceCategory.DOOR_CURVED_WALL_INTERIOR;
  }
  return isExterior ? FaceCategory.DOOR_EXTERIOR : FaceCategory.DOOR_INTERIOR;
}

/**
 * Material remapping table for non-primary doors.
 * Non-primary doors (e.g., secondary entry doors) use trim materials instead of door materials.
 */
export const toPrimaryMaterialMappingTable: MaterialSlotRemapping = {
  doorExterior0: "trimExterior0",
  doorExterior1: "trimExterior1",
  doorExterior2: "trimExterior2",
  doorExterior3: "trimExterior3",
  doorInterior0: "trimInterior0",
  doorInterior1: "trimInterior1",
  doorInterior2: "trimInterior2",
};

interface BuildingForDoorUnitGeometries extends Pick<Building, "interiorPalette" | "exteriorPalette"> {
  readonly settings?: Pick<HomeSettings, "props">;
}

interface BuildingForDoorGeometries extends BuildingForDoorUnitGeometries, Pick<Building, "doorUnits"> {}

export const allDoorFaceCategories = new Set([
  FaceCategory.DOOR_EXTERIOR,
  FaceCategory.DOOR_INTERIOR,
  FaceCategory.DOOR_CURVED_WALL_EXTERIOR,
  FaceCategory.DOOR_CURVED_WALL_INTERIOR,
  FaceCategory.DOOR_DECORATION,
]);

export function getDoorUnitGeometries(
  building: BuildingForDoorUnitGeometries,
  doorUnit: DoorUnit,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { interiorPalette, exteriorPalette } = building;
  const { interiorFront, interiorBack, exteriorFront, exteriorBack, decorations, doorPlacement } = doorUnit;
  const { pickHandle } = doorUnit;

  const entityHandleForSelectingReportingUnit =
    roomEntityHandleForSelectingReportingUnit(doorPlacement.frontRoom) ??
    roomEntityHandleForSelectingReportingUnit(doorPlacement.backRoom);

  const levelId = doorPlacement.frontRoom.level.id;
  const isCurved = isDoorUnitCurved(doorUnit);
  const interiorFaceCategory = isCurved ? FaceCategory.DOOR_CURVED_WALL_INTERIOR : FaceCategory.DOOR_INTERIOR;
  const exteriorFaceCategory = isCurved ? FaceCategory.DOOR_CURVED_WALL_EXTERIOR : FaceCategory.DOOR_EXTERIOR;

  // Handle interior groups (back first, then front - matches old ordering)
  if (entityTypes.has(interiorFaceCategory)) {
    if (doorPlacement.opening?.backRoom) {
      getInteriorGroupsGeometries({
        levelId,
        groups: [interiorBack],
        palette: interiorPalette,
        room: doorPlacement.opening.backRoom,
        faceCategory: interiorFaceCategory,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        emitter,
      });
    }

    if (doorPlacement.opening?.frontRoom) {
      getInteriorGroupsGeometries({
        levelId,
        groups: [interiorFront],
        palette: interiorPalette,
        room: doorPlacement.opening.frontRoom,
        faceCategory: interiorFaceCategory,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        emitter,
      });
    }
  }

  // Handle exterior groups (back first, then front - matches old ordering)
  if (entityTypes.has(exteriorFaceCategory)) {
    getExteriorGroupsGeometries({
      levelId,
      groups: [exteriorBack],
      palette: exteriorPalette,
      faceCategory: exteriorFaceCategory,
      setToExteriorLayer: true,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      viewEntityType: ViewEntityType.DOOR_EXTERIOR,
      emitter,
    });

    getExteriorGroupsGeometries({
      levelId,
      groups: [exteriorFront],
      palette: exteriorPalette,
      faceCategory: exteriorFaceCategory,
      setToExteriorLayer: true,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      viewEntityType: ViewEntityType.DOOR_EXTERIOR,
      emitter,
    });
  }

  // Handle decorations
  if (entityTypes.has(FaceCategory.DOOR_DECORATION)) {
    const { geom: decorationGeom, tags: decorationTags } = decorations;
    for (let i = 0, l = decorationGeom.length; i < l; i++) {
      const { exteriorMaterial, uvGen, viewEntityDescriptor, materialTransformation, exportComponentName } =
        decorationTags[i];
      const geo = decorationGeom[i];

      if (!exteriorMaterial) {
        continue;
      }

      const materialSrc = materialSrcFromPalette(exteriorPalette, exteriorMaterial);
      assertIsNotUndefined(materialSrc);

      const faces = geomToFaces(geo);

      emitter.emit({
        geom: faceGroupNew(faces),
        faceCategory: FaceCategory.DOOR_DECORATION,
        levelId,
        pickHandle,
        entityId: viewEntityDescriptor?.entityId,
        entityHandleForSelectingReportingUnit,
        descriptor: viewEntityDescriptor,
        exportComponentName,
        materialSlot: exteriorMaterial,
        materialSrc,
        uvGen,
        materialTransformation,
        threeLayers: buildExteriorLayers(),
      });
    }
  }

  // Emit props if prop catalog available
  if (emitter.emitProp && building.settings?.props) {
    const { props: propCatalog } = building.settings;
    const { opening, descriptor } = doorPlacement;
    const { isPrimaryDoor } = descriptor;

    // Determine face category for the door prop
    const faceCategory = getDoorFaceCategory(isCurved, doorUnit.prop?.isExterior ?? false);

    // Emit primary door prop (unit.prop)
    if (doorUnit.prop) {
      const prop = doorUnit.prop;
      const isExterior = prop.isExterior ?? false;

      // Compute threeLayers based on exterior vs interior
      const threeLayers = isExterior ? buildExteriorLayers() : buildInteriorLayers();

      // Material remapping for non-primary doors
      const materialRemapping = isPrimaryDoor ? undefined : toPrimaryMaterialMappingTable;

      emitBuildingProp(emitter, prop, {
        threeLayers,
        faceCategory,
        propCatalog,
        levelId,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        materialRemapping,
        roomType: opening?.frontRoom?.type,
      });
    }

    // Emit door accessory props (unit.props) - these use exterior layers, no material remapping
    for (const prop of doorUnit.props) {
      emitBuildingProp(emitter, prop, {
        threeLayers: buildExteriorLayers(),
        faceCategory,
        propCatalog,
        levelId,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        roomType: opening?.frontRoom?.type,
      });
    }
  }
}

export function getDoorGeometries(
  building: BuildingForDoorGeometries,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { doorUnits } = building;
  for (const doorId in doorUnits) {
    const doorUnit = doorUnits[doorId];
    emitter.beginGroup?.("Door", doorId);
    getDoorUnitGeometries(building, doorUnit, entityTypes, emitter);
    emitter.endGroup?.("Door");
  }
}
